import { Box } from '@mui/material'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useNavigate } from 'react-router-dom'
import {
  DocumentTextIcon,
  PlayIcon,
  ArrowDownTrayIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline'
import FAQGuideCard from '../../../components/FAQ/FAQGuideCard'
import FAQAskedQuestionsGamer from '../components/FAQAskedQuestionsGamer'
import { gamerFaqItems } from '../data/faqData'
import { PageHeader } from '../../../shared/components/PageHeader'
import { downloadUserGuide } from '../../../shared/utils/pdfDownload.helper'
import { PageWrapper } from '../../../shared/components'

interface FaqGamerPageProps {
  onNavigateToVideos?: () => void
  className?: string
}

/**
 * FaqGamerPage component - Complete FAQ page for gamer role
 * Follows the same structure as the facilitator FAQ page but with gamer-specific content
 */
export const FaqGamerPage: React.FC<FaqGamerPageProps> = ({ onNavigateToVideos, className = '' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleUserGuideDownload = async () => {
    try {
      await downloadUserGuide()
    } catch (error) {
      // TODO: Show user-friendly error message
    }
  }

  const handleWatchVideos = () => {
    // Navigate to expert videos page
    if (onNavigateToVideos) {
      onNavigateToVideos()
    } else {
      navigate('/gamer/expert-videos')
    }
  }

  const handleWatchTutorials = () => {
    navigate('/gamer/tutorial-videos')
  }

  // const handleTheoryDownload = async () => {
  //   try {
  //     await downloadIntroductionMaterial()
  //   } catch (error) {
  //     // TODO: Show user-friendly error message
  //   }
  // }

  return (
    <PageWrapper className={className}>
      <Box
        sx={{
          maxWidth: '1280px',
          display: 'flex',
          flexDirection: 'column',

          px: { xs: 3, sm: 3, md: 0 }
        }}
      >
        <Box>
          <PageHeader
            badgeText={t('gamer.faq.badge')}
            badgeIcon={<QuestionMarkCircleIcon />}
            title={t('gamer.faq.pageTitle')}
            subtitle={t('gamer.faq.pageSubtitle')}
          />
        </Box>
        {/* Guide Cards Row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            paddingBottom: '32px'
          }}
        >
          <FAQGuideCard
            icon={<DocumentTextIcon className="w-6 h-6" />}
            title={t('gamer.faq.guideCards.userGuide.title')}
            description={t('gamer.faq.guideCards.userGuide.description')}
            buttonText={t('gamer.faq.guideCards.userGuide.button')}
            buttonIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
            onButtonClick={handleUserGuideDownload}
          />
          <FAQGuideCard
            icon={<PlayIcon className="w-6 h-6" />}
            title={t('gamer.faq.guideCards.askExpert.title')}
            description={t('gamer.faq.guideCards.askExpert.description')}
            buttonText={t('gamer.faq.guideCards.askExpert.button')}
            buttonIcon={<PlayIcon className="w-4 h-4" />}
            onButtonClick={handleWatchVideos}
          />
          <FAQGuideCard
            icon={<AcademicCapIcon className="w-6 h-6" />}
            title={t('gamer.faq.guideCards.tutorialVideos.title')}
            description={t('gamer.faq.guideCards.tutorialVideos.description')}
            buttonText={t('gamer.faq.guideCards.tutorialVideos.button')}
            buttonIcon={<AcademicCapIcon className="w-4 h-4" />}
            onButtonClick={handleWatchTutorials}
          />
        </Box>

        {/* FAQ Questions Section */}
        <FAQAskedQuestionsGamer faqItems={gamerFaqItems} />
      </Box>
    </PageWrapper>
  )
}
